import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Download, Clock, AlertTriangle } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";

/**
 * SharedUnifiedBilan · public destination for a signed unified-PDF share link.
 * Route:  /share/unified/:token
 * No auth required — the token itself grants time-limited, download-capped access.
 */
export default function SharedUnifiedBilan() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/unified-share/${token}`);
        setState({ loading: false, data, error: null });
      } catch (err) {
        setState({
          loading: false, data: null,
          error: err?.response?.data?.detail || "Lien invalide.",
        });
      }
    })();
  }, [token]);

  const download = () => {
    // Directly hit the public /pdf endpoint — no auth header needed.
    window.location.href = `${API_BASE}/reports/unified-share/${token}/pdf`;
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] flex items-center justify-center px-6"
         data-testid="shared-unified-bilan-page">
      <div className="max-w-xl w-full rounded-2xl border border-[#E4E1D8] bg-white shadow-lg p-8 md:p-10">
        <div className="flex items-center gap-3">
          <AltioraTreeLogo size={48} />
          <div className="leading-tight">
            <div className="font-display text-xl text-[#0B2545] tracking-tight">ALTIORA</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#7A5B14]">
              Bilan client — lien sécurisé
            </div>
          </div>
        </div>

        {state.loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-[#5B5B62]">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            Vérification du lien…
          </div>
        )}

        {state.error && (
          <div className="mt-8 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
               data-testid="shared-bilan-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 flex-shrink-0" strokeWidth={2} />
            <div>
              <div className="text-sm font-medium text-amber-800">Impossible d'accéder au bilan</div>
              <div className="mt-1 text-xs text-amber-700">{state.error}</div>
            </div>
          </div>
        )}

        {state.data && (
          <div className="mt-8 space-y-5" data-testid="shared-bilan-ready">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
                Bilan pour
              </div>
              <div className="mt-1 font-display text-2xl text-[#0B2545] tracking-tight">
                {state.data.client_name}
              </div>
              {state.data.issuer?.name && (
                <div className="mt-1 text-xs text-[#5B5B62]">
                  Émis par <span className="text-[#0B2545]">{state.data.issuer.name}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InfoTile
                testid="shared-bilan-expires"
                icon={Clock}
                label="Expire"
                value={new Date(state.data.expires_at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" })}
              />
              <InfoTile
                testid="shared-bilan-downloads-left"
                icon={ShieldCheck}
                label="Téléchargements restants"
                value={String(state.data.downloads_left)}
              />
            </div>

            <button
              onClick={download}
              data-testid="shared-bilan-download"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-5 py-3 text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" strokeWidth={2} /> Télécharger le PDF
            </button>

            <p className="text-[11px] text-[#8B8B8F] leading-relaxed">
              Ce lien donne accès uniquement à ce bilan précis, à durée limitée. Aucun compte
              ALTIORA n'est requis. La méthode et les moteurs d'analyse restent la propriété d'ALTIORA COOP.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


function InfoTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-[#E4E1D8] bg-[#FBF6EA]/30 px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[#7A5B14]">
        <Icon className="h-3 w-3" strokeWidth={2} /> {label}
      </div>
      <div className="mt-1 text-sm font-mono text-[#0B2545]">{value}</div>
    </div>
  );
}
